import { ADMIN_PLANS, SUPER_ADMIN_PLANS } from './adminRules';
import { FUNDER_ADMIN_PLANS, FUNDER_GUEST_PLANS, FUNDER_MEMBER_PLANS, FUNDER_OWNER_PLANS } from './funderRulesByPlan';
import {
  INSTITUTION_ADMIN_PLANS,
  INSTITUTION_FIELD_MEMBER_PLANS,
  INSTITUTION_GUEST_PLANS,
  INSTITUTION_MEMBER_PLANS,
  INSTITUTION_OWNER_PLANS,
} from './institutionRulesByPlan';
import {
  SERVICE_PROVIDER_ADMIN_PLANS,
  SERVICE_PROVIDER_FIELD_MEMBER_PLANS,
  SERVICE_PROVIDER_GUEST_PLANS,
  SERVICE_PROVIDER_MEMBER_PLANS,
  SERVICE_PROVIDER_OWNER_PLANS,
} from './serviceProviderRulesByPlan';

const roles: Role[] = [
  'ADMIN',
  'SUPERADMIN',
  'FUNDER_OWNER',
  'FUNDER_ADMIN',
  'FUNDER_MEMBER',
  'FUNDER_GUEST',
  'SERVICE_PROVIDER_OWNER',
  'SERVICE_PROVIDER_ADMIN',
  'SERVICE_PROVIDER_MEMBER',
  'SERVICE_PROVIDER_FIELD_MEMBER',
  'SERVICE_PROVIDER_GUEST',
  'CASE_MANAGEMENT_PRACTITIONER',
  'CASE_MANAGEMENT_ADMINISTRATOR',
  'VISITOR', //not in the db
  'INSTITUTION_OWNER',
  'INSTITUTION_ADMIN',
  'INSTITUTION_MEMBER',
  'INSTITUTION_FIELD_MEMBER',
  'INSTITUTION_GUEST',
];

const plans: Plan[] = ['FREE_VIEWER', 'FREE_EXPLORER', 'FREE', 'STANDARD', 'PREMIUM', 'BUSINESS', 'ENTERPRISE'];

export type Role =
  | 'ADMIN'
  | 'SUPERADMIN'
  | 'VISITOR' //not in the db
  | 'FUNDER_OWNER'
  | 'FUNDER_ADMIN'
  | 'FUNDER_MEMBER'
  | 'FUNDER_GUEST'
  | 'INSTITUTION_OWNER'
  | 'INSTITUTION_ADMIN'
  | 'INSTITUTION_MEMBER'
  | 'INSTITUTION_FIELD_MEMBER'
  | 'INSTITUTION_GUEST'
  | 'SERVICE_PROVIDER_OWNER'
  | 'SERVICE_PROVIDER_ADMIN'
  | 'SERVICE_PROVIDER_MEMBER'
  | 'SERVICE_PROVIDER_FIELD_MEMBER'
  | 'SERVICE_PROVIDER_GUEST'
  | 'CASE_MANAGEMENT_PRACTITIONER'
  | 'CASE_MANAGEMENT_ADMINISTRATOR';

export type UserAddOn = 'REGISTRATION_MANAGEMENT' | 'EMIS_DATA';

export type Plan = 'FREE_VIEWER' | 'FREE_EXPLORER' | 'FREE' | 'STANDARD' | 'PREMIUM' | 'BUSINESS' | 'ENTERPRISE';

export type RuleSet = {
  static: string[];
  dynamic?: {
    [ruleName: string]: Function;
  };
};

export const getUserRole: (role: string | null) => Role = role => {
  const allUserRoles = role && role.split(',');

  for (let i = 0; i < roles.length; i++) {
    if (allUserRoles && allUserRoles.includes(roles[i])) {
      return roles[i];
    }
  }
  return 'VISITOR';
};

export const getUserPlan: (plan: string | null) => Plan = plan => {
  for (let i = 0; i < plans.length; i++) {
    if (plan && plans[i] === plan) {
      return plans[i];
    }
  }
  return 'FREE_VIEWER';
};

export const addOns = {
  REGISTRATION_MANAGEMENT: [
    '/institution/manage/institutions/:institutionId/registrations:visit',
    '/institution/manage/institutions/:institutionId/registrations/register:visit',
    '/institution/manage/institutions/:institutionId/registrations/:ecdRegistrationId:visit',
  ],
};

/*
 * more dynamic way of adding routes
 **/
export const root = {
  serviceProvider: '/service-provider',
  get funderTeamMember() {
    return '/funder/team-members';
  },
  get adminFunders() {
    return '/admin/funders';
  },
  get adminFunderTeamMembers() {
    return '/admin/funders/:funderId/team-members';
  },
  get adminSPTeamMembers() {
    return '/admin/service-providers/:serviceProviderId/team-members';
  },
  get adminProgrammes() {
    return '/admin/programmes';
  },
  get adminInstitutions() {
    return '/admin/institutions';
  },
  get adminInstitutionsTeamMembers() {
    return '/admin/institutions/:institutionId/team-members';
  },
  get adminTeamMember() {
    return '/admin/team-members';
  },
  get adminStakeholder() {
    return '/admin/manage/people';
  },
  get adminServiceProvider() {
    return '/admin/service-providers';
  },
  get adminTemplates() {
    return '/admin/templates';
  },
  get adminDeclarations() {
    return '/admin/declarations';
  },
  get teamMember() {
    return `${this.serviceProvider}/team-members`;
  },
  get teamMemberActivities() {
    return `${this.teamMember}/:stakeholderId/activities`;
  },
  get teamMemberActivitySessions() {
    return `${this.teamMemberActivities}/:activityId/sessions`;
  },
  get adminInsights() {
    return '/admin/manage/insights';
  },
  get SPInsights() {
    return '/service-provider/manage/insights';
  },
  get funderInsights() {
    return '/funder/manage/insights';
  },
  get adminAuditTrail() {
    return '/admin/auditTrail';
  },
};

export type Rules = { [roleName in Role]: any };

export const rules: Rules = {
  VISITOR: {
    static: [
      '/:visit',
      '/admin/sign-in:visit',
      '/funder/sign-in:visit',
      '/institution/sign-in:visit',
      '/service-provider/sign-in:visit',
      '/forgot-password:visit',
    ],
  },
  ADMIN: ADMIN_PLANS,
  SUPERADMIN: SUPER_ADMIN_PLANS,
  FUNDER_OWNER: FUNDER_OWNER_PLANS,
  FUNDER_ADMIN: FUNDER_ADMIN_PLANS,
  FUNDER_MEMBER: FUNDER_MEMBER_PLANS,
  FUNDER_GUEST: FUNDER_GUEST_PLANS,
  INSTITUTION_OWNER: INSTITUTION_OWNER_PLANS,
  INSTITUTION_ADMIN: INSTITUTION_ADMIN_PLANS,
  INSTITUTION_MEMBER: INSTITUTION_MEMBER_PLANS,
  INSTITUTION_FIELD_MEMBER: INSTITUTION_FIELD_MEMBER_PLANS,
  INSTITUTION_GUEST: INSTITUTION_GUEST_PLANS,
  SERVICE_PROVIDER_OWNER: SERVICE_PROVIDER_OWNER_PLANS,
  SERVICE_PROVIDER_ADMIN: SERVICE_PROVIDER_ADMIN_PLANS,
  SERVICE_PROVIDER_MEMBER: SERVICE_PROVIDER_MEMBER_PLANS,
  SERVICE_PROVIDER_FIELD_MEMBER: SERVICE_PROVIDER_FIELD_MEMBER_PLANS,
  SERVICE_PROVIDER_GUEST: SERVICE_PROVIDER_GUEST_PLANS,
  CASE_MANAGEMENT_PRACTITIONER: SERVICE_PROVIDER_ADMIN_PLANS,
  CASE_MANAGEMENT_ADMINISTRATOR: SERVICE_PROVIDER_ADMIN_PLANS,
};

Object.keys(rules).forEach(role => {
  const byPlan = rules[role as Role];
  if (byPlan.FREE_EXPLORER && !byPlan.FREE) {
    byPlan.FREE = byPlan.FREE_EXPLORER;
  }
});

export const computeRulesArray = (plan: Plan, roles: Role[], userAddOns?: UserAddOn[]) => {
  const isValid = roles.every(role => rules[role]);
  const ruleSet: RuleSet = {
    static: [],
  };

  if (!isValid) {
    return ruleSet;
  }

  if (userAddOns && userAddOns.includes('REGISTRATION_MANAGEMENT')) {
    ruleSet.static.concat(addOns['REGISTRATION_MANAGEMENT']);
  }

  roles.forEach(role => {
    const additions = rules[role][plan] ? rules[role][plan].static : rules[role].static;

    ruleSet.static = ruleSet.static.concat(additions);
  });

  return ruleSet;
};

export default rules;
